/**
 * The Diana gate (TASKS T76, BRIEF <spanish_correctness>).
 *
 * > Every generated item carries `vetted: false` regardless of LLM verdict. The UI shows a small
 * > unobtrusive marker on unvetted items and provides a review screen where a human can bulk-flip
 * > items to `vetted: true`. **Machine vetting is a filter, not authority.**
 *
 * The whole corpus ships `vetted: false` — 488 items — so this screen is the only path from
 * "machine-checked" to "a proficient speaker has read it". Two decisions shape it:
 *
 *  1. **A page at a time, not an item at a time.** 488 items one card each is 488 taps, which is a
 *     review nobody finishes; and the brief asks for bulk. The page is sized to what fits on a phone
 *     with the Spanish, the English and the sentence all readable *at once* — see `PAGE_SIZE`.
 *  2. **Approving is explicit; skipping is free.** There is no "reject" that deletes anything. An
 *     item she is unsure about is simply left `vetted: false` and comes back — the gate is a claim
 *     that a human has confirmed this, and the absence of that claim is the safe state.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { ItemRecord } from '../store/schema';

/**
 * How many items one page shows.
 *
 * Three, because that is what fits **above the thumb line at 390×844 without scrolling**, with each
 * item's Spanish, English and carrier sentence all visible at once. Six was the first guess and it
 * put items four to six below the fold — a reviewer who has to scroll to see the last one before
 * tapping "these are right" will approve it unread, which defeats the entire gate. The cost is more
 * pages; the alternative is a rubber stamp.
 */
export const PAGE_SIZE = 3;

export interface VettingRow {
  id: string;
  tier: number;
  /** The Spanish being confirmed. This is the thing she is reading. */
  es: string;
  en: string;
  /** The carrier sentence, where there is one — often where a wrong register actually shows. */
  example: string | null;
  /**
   * What the build-time LLM pass said, in one word, or null where it never ran.
   *
   * Shown as *context*, never as a recommendation, and never pre-ticks anything. Machine vetting is
   * a filter, not authority: an item the model liked still needs a human to say so.
   */
  machineVerdict: string | null;
}

export interface VettingPage {
  rows: VettingRow[];
  /** 0-based. */
  pageIndex: number;
  pageCount: number;
  /** Unvetted items remaining in the whole corpus, this page included. */
  remaining: number;
  /** Items a human has already confirmed. */
  confirmed: number;
}

/**
 * Everything still awaiting a human, in a stable order.
 *
 * Tier then id — the same order the corpus is authored in, so a reviewer works through Núcleo before
 * Frase and the pages do not reshuffle underneath her as items are approved. Sorting by anything
 * that *changes* when an item is vetted would make the next page unpredictable.
 */
export function unvetted(items: readonly ItemRecord[]): ItemRecord[] {
  return items
    .filter((item) => !item.vetted)
    .sort((a, b) => (a.tier !== b.tier ? a.tier - b.tier : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export interface PageInput {
  items: readonly ItemRecord[];
  pageIndex: number;
  /** Carrier sentences, by item id. Looked up by the caller from the corpus (D-112). */
  examples?: ReadonlyMap<string, string>;
  pageSize?: number;
}

/**
 * One page of items to read.
 *
 * The index is clamped rather than refused: approving the last page leaves the index past the end,
 * and the honest response to that is the last page, not an error.
 */
export function vettingPage(input: PageInput): VettingPage {
  const size = input.pageSize ?? PAGE_SIZE;
  const pending = unvetted(input.items);
  const confirmed = input.items.length - pending.length;
  const pageCount = Math.max(1, Math.ceil(pending.length / size));
  const pageIndex = Math.min(Math.max(0, input.pageIndex), pageCount - 1);

  const rows = pending.slice(pageIndex * size, pageIndex * size + size).map(
    (item): VettingRow => ({
      id: item.id,
      tier: item.tier,
      es: item.es,
      en: item.en,
      example: input.examples?.get(item.id) ?? null,
      machineVerdict: verdictWord(item),
    }),
  );

  return { rows, pageIndex, pageCount, remaining: pending.length, confirmed };
}

/**
 * The machine's opinion, in one word.
 *
 * Deliberately coarse. A detailed verdict beside each line would be read as a recommendation, and
 * the one thing this screen exists to establish is that a human looked.
 */
function verdictWord(item: ItemRecord): string | null {
  if (!item.verdict) return null;
  const { grammatical, natural, mexican } = item.verdict;
  return grammatical && natural && mexican ? 'machine: ok' : 'machine: flagged';
}

/** The records as they should be stored once a human has confirmed them. */
export function markVetted(items: readonly ItemRecord[], ids: readonly string[]): ItemRecord[] {
  const wanted = new Set(ids);
  return items.filter((item) => wanted.has(item.id)).map((item) => ({ ...item, vetted: true }));
}

/**
 * How the corpus stands, for the one line the screen shows.
 *
 * A count and a fraction, never a percentage bar and never a "well done": this is a workload, and it
 * belongs to somebody who is doing the reviewer a favour.
 */
export function vettingProgress(page: VettingPage): string {
  if (page.remaining === 0) return 'Everything has been checked.';
  const total = page.confirmed + page.remaining;
  return `${page.confirmed} of ${total} checked · ${page.remaining} to go`;
}
